import {
  ListCustomFieldsRequest,
  TargetEntityType,
} from "../CustomFieldRecord";

/**
 * TanStack-Query key factory for custom-field reads.
 *
 *   all(org) ⊃ lists(org) ⊃ listsForEntity(org, type) ⊃ { list, readyList }
 *
 * Org-scoped at the root because the query cache is not cleared on org switch,
 * Segmented by entity type so a mutation invalidates only the entity type it changed:
 * `listsForEntity` is the invalidation handle, a prefix of both per-entity
 * leaves, so invalidating it refetches every cached list for that type.
 */
export const customFieldQueryKeys = {
  all: (orgId: string) => ["customFields", orgId] as const,

  lists: (orgId: string) =>
    [...customFieldQueryKeys.all(orgId), "list"] as const,

  listsForEntity: (orgId: string, entityType?: TargetEntityType) =>
    [...customFieldQueryKeys.lists(orgId), entityType] as const,

  // Used by list requests that specify arbitrary custom field statuses (e.g. Settings).
  // Distinct from the Ready-only `readyList` below — see the note there.
  list: (orgId: string, request: ListCustomFieldsRequest) =>
    [
      ...customFieldQueryKeys.listsForEntity(orgId, request.entity_type),
      request,
    ] as const,

  // Ready-only list, used by data grids. Deliberately its own key rather than
  // list(org, { statuses: [Ready] }): the two consumers cache different shapes
  // (useReadyCustomFields → CustomFieldRecord[]; useCustomFields → a
  // PaginatedResponse) under different fetch policies (Ready: no poll + 60s
  // stale; all-statuses: polls while a field is transitional), so they must not
  // share one cache entry.
  readyList: (orgId: string, entityType: TargetEntityType) =>
    [
      ...customFieldQueryKeys.listsForEntity(orgId, entityType),
      "ready",
    ] as const,
};
